import { getCurrentLocalTimestamp, localNowDateString } from '@/app/utils/localDate';
import { mapPool } from '@/app/utils/perfTiming';
import { supabase } from '@/lib/supabase';
import { getDocumentConversionSchemaFlags } from '@/app/lib/documentConversionSchema';
import { canPostAccountingForPurchaseStatus, wasPurchasePostedForReversal } from '@/app/lib/postingStatusGate';
import { activityLogService } from '@/app/services/activityLogService';
import { dispatchContactBalancesRefresh } from '@/app/lib/contactBalancesRefresh';
import { dispatchAccountingInvalidated } from '@/app/lib/dataInvalidationBus';
import { createSupplierPayment } from '@/app/services/supplierPaymentService';
import { PURCHASE_HEADER_COLUMNS } from '@/app/lib/purchaseDbConstants';
import { postPurchaseDocumentAccounting, reversePurchaseDocumentAccounting } from '@/app/services/documentPostingEngine';
import { documentNumberService } from '@/app/services/documentNumberService';
import {
  syncJournalEntryDateByDocumentRefs,
  syncJournalEntryDateByPaymentId,
} from '@/app/services/journalTransactionDateSyncService';
import type { AuditAction } from '@/app/services/auditLogService';
import { logPurchaseAuditNonBlocking } from '@/app/services/auditLogService';

function purchaseRowNeedsPostedPoAllocation(row: Record<string, unknown>): boolean {
  const po = String(row.po_no ?? '').trim();
  if (!po) return true;
  return /^(PDR-|POR-)/i.test(po);
}

function isUniquePoNumberViolation(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as {
    code?: string;
    message?: string;
    details?: string;
    hint?: string;
    status?: number;
    statusCode?: number;
  };
  const code = String(e.code || '');
  const status = Number(e.status ?? e.statusCode ?? 0);
  const blob = `${e.message || ''} ${e.details || ''} ${e.hint || ''}`.toLowerCase();
  const looksUnique =
    code === '23505' ||
    status === 409 ||
    blob.includes('duplicate key') ||
    blob.includes('unique constraint') ||
    blob.includes('already exists');
  if (!looksUnique) return false;
  // Prefer document-number collisions; still retry on sparse PostgREST 409 bodies.
  return (
    blob.includes('po_no') ||
    blob.includes('draft_no') ||
    blob.includes('order_no') ||
    blob.includes('idx_purchases_company_po_no') ||
    code === '23505' ||
    status === 409
  );
}

async function reallocatePurchaseDocumentNumbers(
  purchase: Purchase,
  row: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const companyId = String(purchase.company_id ?? row.company_id ?? '');
  if (!companyId) return row;
  const status = String(purchase.status ?? row.status ?? 'draft');
  const seqType = status === 'draft' ? 'PDR' : status === 'ordered' ? 'POR' : 'PUR';
  const purchaseNo = await documentNumberService.getNextDocumentNumberGlobal(companyId, seqType);
  const posted = canPostAccountingForPurchaseStatus(status);
  purchase.po_no = posted ? purchaseNo : null;
  purchase.draft_no = status === 'draft' ? purchaseNo : null;
  purchase.order_no = status === 'ordered' ? purchaseNo : null;
  return {
    ...row,
    po_no: posted ? purchaseNo : null,
    draft_no: status === 'draft' ? purchaseNo : null,
    order_no: status === 'ordered' ? purchaseNo : null,
  };
}

/** PostgREST / proxy 5xx — must not be reported as "Purchase not found" (misleading when API is down). */
function isTransientSupabaseHttpError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { message?: string; status?: number; statusCode?: number };
  const status = Number(e.status ?? e.statusCode ?? 0);
  if (status >= 500 && status < 600) return true;
  const msg = String(e.message || '').toLowerCase();
  return (
    msg.includes('bad gateway') ||
    msg.includes('gateway timeout') ||
    msg.includes('service unavailable') ||
    msg.includes('cloudflare') ||
    /\b50[234]\b/.test(msg)
  );
}

/** Allocate PUR po_no when purchase becomes posted if missing or still PDR/POR in po_no. */
async function ensurePostedPurchasePoNoAllocated(purchaseId: string, row: Record<string, unknown>) {
  if (!purchaseRowNeedsPostedPoAllocation(row)) return row;
  const companyId = String(row.company_id ?? '');
  if (!companyId) throw new Error('Cannot post purchase: company_id missing.');
  let nextNo: string;
  try {
    nextNo = await documentNumberService.getNextDocumentNumberGlobal(companyId, 'PUR');
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`Cannot post purchase: could not allocate PO number (PUR). ${msg}`);
  }
  const { data: patched, error } = await supabase
    .from('purchases')
    .update({ po_no: nextNo })
    .eq('id', purchaseId)
    .select(PURCHASE_HEADER_COLUMNS)
    .single();
  if (error) throw new Error(`Cannot post purchase: failed to save po_no: ${error.message}`);
  return (patched ?? { ...row, po_no: nextNo }) as Record<string, unknown>;
}

/** Enrich purchases with creator full_name. purchases.created_by = auth.users.id; resolve via users.auth_user_id. */
async function enrichPurchasesWithCreatorNames(purchases: any[]): Promise<void> {
  const ids = [...new Set((purchases || []).map((p: any) => p.created_by).filter(Boolean))] as string[];
  if (ids.length === 0) return;
  const nameByCreatedBy = new Map<string, string>();
  const { data: usersByAuth } = await supabase.from('users').select('auth_user_id, full_name, email').in('auth_user_id', ids);
  (usersByAuth || []).forEach((u: any) => {
    if (u?.auth_user_id) nameByCreatedBy.set(u.auth_user_id, u.full_name || u.email || '');
  });
  const missing = ids.filter((id) => !nameByCreatedBy.has(id));
  if (missing.length > 0) {
    const { data: usersById } = await supabase.from('users').select('id, full_name, email').in('id', missing);
    (usersById || []).forEach((u: any) => {
      if (u?.id) nameByCreatedBy.set(u.id, u.full_name || u.email || '');
    });
  }
  purchases.forEach((p: any) => {
    const uid = p.created_by;
    if (uid && typeof uid === 'string') {
      const name = nameByCreatedBy.get(uid) || null;
      p.created_by_user = { full_name: name, email: null };
    }
  });
}

/** Attach supplier contact when list embed failed (PGRST201). */
async function enrichPurchasesWithSupplierContacts(purchases: any[]): Promise<void> {
  const ids = [...new Set((purchases || []).map((p: any) => p.supplier_id).filter(Boolean))] as string[];
  if (ids.length === 0) return;
  const { data: contacts } = await supabase.from('contacts').select('id, name, phone').in('id', ids);
  const byId = new Map((contacts || []).map((c: any) => [c.id, c]));
  purchases.forEach((p: any) => {
    if (p.supplier_id && !p.supplier) {
      const c = byId.get(p.supplier_id);
      if (c) p.supplier = { name: c.name, phone: c.phone };
    }
  });
}

/** Header-only list: attach branch { id, name } from branch_id so mapper can show location. */
async function enrichPurchasesWithBranchNames(purchases: any[]): Promise<void> {
  const ids = [
    ...new Set(
      (purchases || [])
        .map((p: any) => p.branch_id)
        .filter((id: unknown) => typeof id === 'string' && id.length > 0),
    ),
  ] as string[];
  if (ids.length === 0) return;
  const { data: branches } = await supabase.from('branches').select('id, name, code').in('id', ids);
  const byId = new Map((branches || []).map((b: any) => [b.id, b]));
  purchases.forEach((p: any) => {
    if (!p.branch && p.branch_id) {
      const b = byId.get(p.branch_id);
      if (b) p.branch = { id: b.id, name: b.name, code: b.code };
    }
  });
}

/** Header-only list: set items_count from purchase_items (no full embed). */
async function enrichPurchasesWithItemCounts(purchases: any[]): Promise<void> {
  const ids = (purchases || []).map((p: any) => p.id).filter(Boolean) as string[];
  if (ids.length === 0) return;
  const needsCount = (purchases || []).some(
    (p: any) => !Array.isArray(p.items) || p.items.length === 0,
  );
  if (!needsCount) return;

  const { data: rows, error } = await supabase
    .from('purchase_items')
    .select('purchase_id')
    .in('purchase_id', ids);
  if (error || !rows) return;

  const countByPurchase = new Map<string, number>();
  for (const row of rows as { purchase_id?: string }[]) {
    const pid = row.purchase_id;
    if (!pid) continue;
    countByPurchase.set(pid, (countByPurchase.get(pid) || 0) + 1);
  }
  purchases.forEach((p: any) => {
    if (!p?.id) return;
    if (Array.isArray(p.items) && p.items.length > 0) return;
    const n = countByPurchase.get(p.id) || 0;
    p.items_count = n;
  });
}

function buildPaymentNote(extraDescription?: string | null, bankTraceId?: string | null): string | null {
  const parts: string[] = [];
  const extra = String(extraDescription ?? '').trim();
  const trace = String(bankTraceId ?? '').trim();
  if (extra) parts.push(extra);
  if (trace) parts.push(`Bank Trace ID: ${trace}`);
  return parts.length ? parts.join(' | ') : null;
}

export interface Purchase {
  id?: string;
  company_id: string;
  branch_id: string;
  po_no?: string | null;
  /** Same-row lifecycle: draft stage number; po_no is final-only. */
  draft_no?: string | null;
  order_no?: string | null;
  po_date: string;
  supplier_id?: string;
  supplier_name: string;
  status: 'draft' | 'ordered' | 'received' | 'final' | 'cancelled'; // Database enum values only
  payment_status: 'paid' | 'partial' | 'unpaid';
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  shipping_cost: number;
  freight_settlement?: 'supplier' | 'courier';
  clearance_courier_id?: string | null;
  total: number;
  paid_amount: number;
  due_amount: number;
  notes?: string;
  attachments?: { url: string; name: string }[] | null;
  created_by: string;
  /** Import FX metadata — null when multiCurrencyEnabled OFF. GL still uses PKR totals. */
  document_currency?: string | null;
  fx_rate_to_base?: number | null;
  foreign_subtotal?: number | null;
  foreign_total?: number | null;
}

export interface PurchaseItem {
  product_id: string;
  variation_id?: string;
  product_name: string;
  sku: string; // Required in DB
  quantity: number;
  unit?: string;
  unit_price: number;
  /** Only discount_amount is stored in DB; percentage is UI-only. */
  discount_amount?: number;
  tax_amount?: number;
  total: number;
  // Packing fields
  packing_type?: string;
  packing_quantity?: number;
  packing_unit?: string;
  packing_details?: any; // JSONB
  notes?: string;
  foreign_unit_price?: number | null;
  foreign_line_total?: number | null;
}

/** One row per charge (freight, loading, discount, etc.) for audit-ready ledger. */
export interface PurchaseChargeInsert {
  charge_type: string;
  amount: number;
  ledger_account_id?: string | null;
}

/** Allowed columns for purchases insert. DB has discount_amount only (no discount_percentage). */
const PURCHASE_INSERT_KEYS = [
  'company_id', 'branch_id', 'po_no', 'draft_no', 'order_no', 'po_date', 'supplier_id', 'supplier_name',
  'status', 'payment_status', 'subtotal', 'discount_amount', 'tax_amount', 'shipping_cost',
  'freight_settlement', 'clearance_courier_id',
  'total', 'paid_amount', 'due_amount', 'notes', 'attachments', 'created_by',
  'document_currency', 'fx_rate_to_base', 'foreign_subtotal', 'foreign_total',
] as const;

function pickPurchaseRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of PURCHASE_INSERT_KEYS) {
    if (row[k] !== undefined) out[k] = row[k];
  }
  return out;
}

/** Build purchase row for insert. Only discount_amount is stored; percentage is UI-only and never sent. */
function buildPurchaseInsertRow(purchase: Purchase): Record<string, unknown> {
  const discountAmount = Number((purchase as any).discount_amount ?? (purchase as any).discount ?? 0) || 0;
  const posted = canPostAccountingForPurchaseStatus(purchase.status);
  return pickPurchaseRow({
    company_id: purchase.company_id,
    branch_id: purchase.branch_id,
    po_no: posted ? purchase.po_no ?? null : null,
    draft_no: purchase.draft_no ?? null,
    order_no: purchase.order_no ?? null,
    po_date: purchase.po_date,
    supplier_id: purchase.supplier_id ?? null,
    supplier_name: purchase.supplier_name,
    status: purchase.status,
    payment_status: purchase.payment_status,
    subtotal: purchase.subtotal ?? 0,
    discount_amount: discountAmount,
    tax_amount: purchase.tax_amount ?? 0,
    shipping_cost: purchase.shipping_cost ?? 0,
    total: purchase.total ?? 0,
    paid_amount: purchase.paid_amount ?? 0,
    due_amount: purchase.due_amount ?? 0,
    notes: purchase.notes ?? null,
    attachments: purchase.attachments ?? null,
    created_by: purchase.created_by,
    document_currency: purchase.document_currency ?? null,
    fx_rate_to_base: purchase.fx_rate_to_base ?? null,
    foreign_subtotal: purchase.foreign_subtotal ?? null,
    foreign_total: purchase.foreign_total ?? null,
  });
}

/** Allowed columns for purchase_items insert (DB has no discount_percentage/tax_percentage). */
const PURCHASE_ITEM_INSERT_KEYS = [
  'purchase_id', 'product_id', 'variation_id', 'product_name', 'sku', 'quantity', 'unit',
  'unit_price', 'discount_amount', 'tax_amount', 'total',
  'packing_type', 'packing_quantity', 'packing_unit', 'packing_details', 'notes',
  'foreign_unit_price', 'foreign_line_total',
] as const;

function pickPurchaseItemRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of PURCHASE_ITEM_INSERT_KEYS) {
    if (row[k] !== undefined) out[k] = row[k];
  }
  return out;
}

/** Build purchase_items row (full schema: discount_amount, tax_amount, sku, unit). No discount_percentage/tax_percentage. */
function buildPurchaseItemInsertRow(item: PurchaseItem, purchaseId: string): Record<string, unknown> {
  const discountVal = Number(item.discount_amount ?? (item as any).discount ?? 0) || 0;
  const taxVal = Number(item.tax_amount ?? (item as any).tax ?? 0) || 0;
  const quantity = Number(item.quantity) || 0;
  const unitPrice = Number(item.unit_price) || 0;
  const total = Number(item.total) || 0;
  return pickPurchaseItemRow({
    purchase_id: purchaseId,
    product_id: String(item.product_id || '').trim() || null,
    variation_id: item.variation_id ?? null,
    product_name: String(item.product_name || '').trim() || 'Unknown',
    sku: item.sku ?? 'N/A',
    quantity,
    unit: item.unit ?? 'pcs',
    unit_price: unitPrice,
    discount_amount: discountVal,
    tax_amount: taxVal,
    total,
    packing_type: item.packing_type ?? null,
    packing_quantity: item.packing_quantity != null ? Number(item.packing_quantity) : null,
    packing_unit: item.packing_unit ?? null,
    packing_details: item.packing_details != null ? item.packing_details : null,
    notes: item.notes ?? null,
    foreign_unit_price: item.foreign_unit_price ?? null,
    foreign_line_total: item.foreign_line_total ?? null,
  });
}

/** Build purchase_items row for older schema (discount, tax; no sku, unit). Use when full schema insert returns 400. */
function buildPurchaseItemInsertRowMinimal(item: PurchaseItem, purchaseId: string): Record<string, unknown> {
  const discountVal = Number(item.discount_amount ?? (item as any).discount ?? 0) || 0;
  const taxVal = Number(item.tax_amount ?? (item as any).tax ?? 0) || 0;
  return {
    purchase_id: purchaseId,
    product_id: String(item.product_id || '').trim() || null,
    variation_id: item.variation_id ?? null,
    product_name: String(item.product_name || '').trim() || 'Unknown',
    quantity: Number(item.quantity) || 0,
    unit_price: Number(item.unit_price) || 0,
    discount: discountVal,
    tax: taxVal,
    total: Number(item.total) || 0,
    packing_type: item.packing_type ?? null,
    packing_quantity: item.packing_quantity != null ? Number(item.packing_quantity) : null,
    packing_unit: item.packing_unit ?? null,
    packing_details: item.packing_details != null ? item.packing_details : null,
    notes: item.notes ?? null,
  };
}

function roundCost(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 10000) / 10000;
}

function computeWeightedCost(currentQty: number, currentCost: number, incomingQty: number, incomingCost: number): number {
  const safeIncomingQty = Number(incomingQty) || 0;
  const safeIncomingCost = Number(incomingCost) || 0;
  if (safeIncomingQty <= 0 || safeIncomingCost <= 0) return roundCost(currentCost);
  const safeCurrentQty = Math.max(0, Number(currentQty) || 0);
  const safeCurrentCost = Math.max(0, Number(currentCost) || 0);
  const denominator = safeCurrentQty + safeIncomingQty;
  if (denominator <= 0) return roundCost(safeIncomingCost);
  return roundCost(((safeCurrentQty * safeCurrentCost) + (safeIncomingQty * safeIncomingCost)) / denominator);
}

function isMissingColumnError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  const message = String(e.message || '').toLowerCase();
  return e.code === '42703' || (message.includes('column') && message.includes('does not exist'));
}

type CostRollupItem = {
  product_id: string;
  variation_id?: string | null;
  quantity: number;
  unit_price: number;
};

async function applyWeightedCostRollup(params: {
  companyId: string;
  branchId?: string | null;
  purchaseId?: string;
  items: CostRollupItem[];
}): Promise<void> {
  const { companyId, branchId, purchaseId, items } = params;
  const validItems = (items || []).filter((it) => Number(it.quantity) > 0 && Number(it.unit_price) > 0 && it.product_id);
  if (!companyId || validItems.length === 0) return;

  const productIncoming = new Map<string, { qty: number; sum: number }>();
  const variationIncoming = new Map<string, { productId: string; qty: number; sum: number }>();
  for (const item of validItems) {
    const qty = Number(item.quantity) || 0;
    const cost = Number(item.unit_price) || 0;
    const key = String(item.product_id);
    const prevProduct = productIncoming.get(key) ?? { qty: 0, sum: 0 };
    prevProduct.qty += qty;
    prevProduct.sum += qty * cost;
    productIncoming.set(key, prevProduct);
    if (item.variation_id) {
      const vKey = String(item.variation_id);
      const prevVar = variationIncoming.get(vKey) ?? { productId: key, qty: 0, sum: 0 };
      prevVar.qty += qty;
      prevVar.sum += qty * cost;
      variationIncoming.set(vKey, prevVar);
    }
  }

  const productIds = Array.from(productIncoming.keys());
  if (productIds.length === 0) return;

  // Current product costs
  const { data: products } = await supabase
    .from('products')
    .select('id, cost_price')
    .in('id', productIds);
  const productCostById = new Map<string, number>((products || []).map((p: any) => [String(p.id), Number(p.cost_price) || 0]));

  // Current variation costs (schema tolerant)
  const variationIds = Array.from(variationIncoming.keys());
  const variationCostById = new Map<string, number>();
  if (variationIds.length > 0) {
    let variationRows: any[] = [];
    const full = await supabase.from('product_variations').select('id, product_id, purchase_price, cost_price').in('id', variationIds);
    if (!full.error && full.data) {
      variationRows = full.data as any[];
    } else if (isMissingColumnError(full.error)) {
      const fallback = await supabase.from('product_variations').select('id, product_id, cost_price').in('id', variationIds);
      if (!fallback.error && fallback.data) {
        variationRows = fallback.data as any[];
      } else {
        const minimal = await supabase.from('product_variations').select('id, product_id').in('id', variationIds);
        variationRows = (minimal.data as any[]) || [];
      }
    }
    for (const row of variationRows) {
      const c = Number((row as any).purchase_price ?? (row as any).cost_price) || 0;
      variationCostById.set(String((row as any).id), c);
    }
  }

  // Stock snapshot excluding current purchase rows so weighted math is deterministic.
  let stockQuery = supabase
    .from('stock_movements')
    .select('product_id, variation_id, quantity, reference_type, reference_id')
    .eq('company_id', companyId)
    .in('product_id', productIds);
  if (branchId && branchId !== 'all') stockQuery = stockQuery.eq('branch_id', branchId);
  const { data: stockRows } = await stockQuery;
  const stockByProduct = new Map<string, number>();
  const stockByVariation = new Map<string, number>();
  for (const row of stockRows || []) {
    const refType = String((row as any).reference_type || '');
    const refId = String((row as any).reference_id || '');
    if (purchaseId && refType === 'purchase' && refId === purchaseId) continue;
    const q = Number((row as any).quantity) || 0;
    const pId = String((row as any).product_id || '');
    const vId = (row as any).variation_id ? String((row as any).variation_id) : '';
    if (!pId) continue;
    if (vId) stockByVariation.set(vId, (stockByVariation.get(vId) || 0) + q);
    stockByProduct.set(pId, (stockByProduct.get(pId) || 0) + q);
  }

  // Update variation weighted costs (bounded concurrency — avoid N sequential round-trips)
  const variationUpdates = [...variationIncoming.entries()]
    .map(([variationId, incoming]) => {
      const currentQty = Math.max(0, stockByVariation.get(variationId) || 0);
      const currentCost = variationCostById.get(variationId) || 0;
      const incomingCost = incoming.sum / Math.max(1, incoming.qty);
      const next = computeWeightedCost(currentQty, currentCost, incoming.qty, incomingCost);
      return next > 0 ? { variationId, next } : null;
    })
    .filter((x): x is { variationId: string; next: number } => x != null);

  await mapPool(variationUpdates, 5, async ({ variationId, next }) => {
    const upFull = await supabase
      .from('product_variations')
      .update({ purchase_price: next, cost_price: next })
      .eq('id', variationId);
    if (upFull.error && isMissingColumnError(upFull.error)) {
      const upCost = await supabase.from('product_variations').update({ cost_price: next }).eq('id', variationId);
      if (upCost.error && isMissingColumnError(upCost.error)) {
        await supabase.from('product_variations').update({ purchase_price: next }).eq('id', variationId);
      }
    }
  });

  // Update parent product weighted costs
  const productUpdates = [...productIncoming.entries()]
    .map(([productId, incoming]) => {
      const currentQty = Math.max(0, stockByProduct.get(productId) || 0);
      const currentCost = productCostById.get(productId) || 0;
      const incomingCost = incoming.sum / Math.max(1, incoming.qty);
      const next = computeWeightedCost(currentQty, currentCost, incoming.qty, incomingCost);
      return next > 0 ? { productId, next } : null;
    })
    .filter((x): x is { productId: string; next: number } => x != null);

  await mapPool(productUpdates, 5, async ({ productId, next }) => {
    await supabase.from('products').update({ cost_price: next, updated_at: getCurrentLocalTimestamp() }).eq('id', productId);
  });
}

export const purchaseService = {
  // Create purchase with items and optional per-line charges (for line-by-line ledger)
  async createPurchase(
    purchase: Purchase,
    items: PurchaseItem[],
    charges?: PurchaseChargeInsert[],
    _options?: Record<string, never>
  ) {
    let purchaseRow = buildPurchaseInsertRow(purchase);

    let purchaseData: Record<string, unknown> | null = null;
    let purchaseError: unknown = null;
    /** Match sale finalize retries — sequence can lag behind existing PUR/PDR/POR rows. */
    const maxPoAttempts = 12;
    let lastConflictNo: string | undefined;
    for (let attempt = 0; attempt < maxPoAttempts; attempt++) {
      const res = await supabase.from('purchases').insert(purchaseRow).select('*').single();
      purchaseData = res.data as Record<string, unknown> | null;
      purchaseError = res.error;
      if (!purchaseError) break;
      if (isUniquePoNumberViolation(purchaseError)) {
        lastConflictNo = String(
          purchaseRow.po_no ?? purchaseRow.draft_no ?? purchaseRow.order_no ?? '',
        ).trim() || undefined;
        if (import.meta.env?.DEV) {
          console.warn(
            `[PURCHASE SERVICE] Document number conflict (attempt ${attempt + 1}/${maxPoAttempts}), last=`,
            lastConflictNo,
            purchaseError,
          );
        }
        purchaseRow = await reallocatePurchaseDocumentNumbers(purchase, purchaseRow);
        continue;
      }
      break;
    }

    if (purchaseError) {
      console.error('[PURCHASE SERVICE] Error creating purchase:', purchaseError);
      if (isUniquePoNumberViolation(purchaseError)) {
        throw new Error(
          `Purchase document number still conflicts after ${maxPoAttempts} attempts` +
            (lastConflictNo ? ` (last tried: ${lastConflictNo})` : '') +
            '. Check Settings → Numbering sequences for PUR/PDR/POR, then retry.',
        );
      }
      throw purchaseError;
    }
    if (!purchaseData) {
      throw new Error('Failed to create purchase.');
    }

    const itemsWithPurchaseId = items.map(item => buildPurchaseItemInsertRow(item, purchaseData!.id as string));

    let itemsError: any = null;
    let res = await supabase.from('purchase_items').insert(itemsWithPurchaseId);
    itemsError = res.error;

    // If 400 (e.g. column does not exist), retry with minimal schema (discount/tax, no sku/unit)
    if (itemsError && (itemsError.code === '400' || itemsError.status === 400)) {
      const minimalItems = items.map(item => buildPurchaseItemInsertRowMinimal(item, purchaseData.id));
      res = await supabase.from('purchase_items').insert(minimalItems);
      itemsError = res.error;
    }

    if (itemsError) {
      await supabase.from('purchases').delete().eq('id', purchaseData.id);
      throw itemsError;
    }

    await applyWeightedCostRollup({
      companyId: String((purchaseData as any).company_id || purchase.company_id),
      branchId: (purchaseData as any).branch_id ?? purchase.branch_id ?? null,
      purchaseId: String((purchaseData as any).id || ''),
      items: items.map((item) => ({
        product_id: String(item.product_id),
        variation_id: item.variation_id ?? null,
        quantity: Number(item.quantity) || 0,
        unit_price: Number(item.unit_price) || 0,
      })),
    });

    // Insert purchase_charges (one row per extra expense / discount) for line-by-line accounting
    if (charges && charges.length > 0) {
      const chargeRows = charges
        .filter((c) => c.amount > 0)
        .map((c) => ({
          purchase_id: purchaseData.id,
          charge_type: c.charge_type,
          ledger_account_id: c.ledger_account_id ?? null,
          amount: Number(c.amount),
          created_by: purchase.created_by ?? null,
        }));
      if (chargeRows.length > 0) {
        const { error: chargesError } = await supabase.from('purchase_charges').insert(chargeRows);
        if (chargesError) {
          console.warn('[PURCHASE SERVICE] purchase_charges insert failed (table may not exist):', chargesError);
          // Do not rollback purchase; header/items are saved. Accounting can use header totals as fallback.
        }
      }
    }

    if ((purchaseData as any)?.company_id) {
      dispatchContactBalancesRefresh(String((purchaseData as any).company_id));
    }

    const pd = purchaseData as { id?: string; company_id?: string; po_no?: unknown; status?: unknown };
    if (pd?.id && pd.company_id) {
      logPurchaseAuditNonBlocking(String(pd.company_id), pd.id, 'created', {
        po_no: pd.po_no,
        status: pd.status,
      });
    }

    return purchaseData;
  },

  // Get all purchases (no created_by_user:users join – production DB may have no FK purchases→users; PGRST200)
  async getAllPurchases(
    companyId: string,
    branchId?: string,
    opts?: { limit?: number; offset?: number }
  ): Promise<any[] | { data: any[]; total: number }> {
    const limit = opts?.limit ?? 50;
    const offset = opts?.offset ?? 0;
    // Disambiguate contacts embed: purchases has FKs supplier_id + clearance_courier_id → contacts (PGRST201).
    const purchaseSelect = `
        *,
        supplier:contacts!purchases_supplier_id_fkey(name, phone),
        branch:branches(id, name, code),
        items:purchase_items(
          *,
          product:products(name)
        )
      `;
    const schemaFlags = await getDocumentConversionSchemaFlags();
    const runPurchList = (hideConverted: boolean) => {
      let q = supabase
        .from('purchases')
        .select(purchaseSelect, opts ? { count: 'exact' } : undefined)
        .eq('company_id', companyId)
        .order('po_date', { ascending: false });
      if (hideConverted && schemaFlags.purchasesConvertedColumn) q = q.eq('converted', false);
      if (branchId) q = q.eq('branch_id', branchId);
      if (opts) q = q.range(offset, offset + limit - 1);
      return q;
    };

    let { data, error, count } = await runPurchList(true);
    if (error && schemaFlags.purchasesConvertedColumn) {
      const retry = await runPurchList(false);
      if (!retry.error) {
        data = retry.data;
        error = retry.error;
        count = retry.count;
      }
    }

    // If error is about po_date column not existing, retry with created_at
    if (error && error.code === '42703' && error.message?.includes('po_date')) {
      const retryQuery = supabase
        .from('purchases')
        .select(`
          *,
          supplier:contacts!purchases_supplier_id_fkey(name, phone),
          branch:branches(id, name, code),
          items:purchase_items(
            *,
            product:products(name)
          )
        `)
        .order('created_at', { ascending: false });
      
      if (branchId) {
        retryQuery.eq('branch_id', branchId);
      }
      
      const { data: retryData, error: retryError } = await retryQuery;
      if (!retryError && retryData?.length) await enrichPurchasesWithCreatorNames(retryData);
      if (retryError) {
        // If created_at also doesn't exist, try without ordering
        const finalQuery = supabase
          .from('purchases')
        .select(`
          *,
          supplier:contacts!purchases_supplier_id_fkey(name, phone),
          branch:branches(id, name, code),
          items:purchase_items(
            *,
            product:products(name)
          )
        `);
        
        if (branchId) {
          finalQuery.eq('branch_id', branchId);
        }
        
        const { data: finalData, error: finalError } = await finalQuery;
        if (finalError) throw finalError;
        if (finalData?.length) await enrichPurchasesWithCreatorNames(finalData);
        return opts ? { data: finalData || [], total: finalData?.length ?? 0 } : finalData;
      }
      return opts ? { data: retryData || [], total: retryData?.length ?? 0 } : retryData;
    }
    
    // Ambiguous FK / missing nested relationship: fall back to header-only (+ optional supplier enrich)
    if (error && (error.code === '42703' || error.code === '42P01' || error.code === 'PGRST116' || error.code === 'PGRST201' || error.code === 'PGRST200')) {
      let simpleQuery = supabase
        .from('purchases')
        .select('*', opts ? { count: 'exact' } : undefined)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (branchId) {
        simpleQuery = simpleQuery.eq('branch_id', branchId);
      }
      if (opts) simpleQuery = simpleQuery.range(offset, offset + limit - 1);
      
      let { data: simpleData, error: simpleError, count: simpleCount } = await simpleQuery;
      
      // If created_at doesn't exist, try without ordering
      if (simpleError && simpleError.code === '42703' && simpleError.message?.includes('created_at')) {
        let noOrderQuery = supabase
          .from('purchases')
          .select('*', opts ? { count: 'exact' } : undefined)
          .eq('company_id', companyId);
        
        if (branchId) {
          noOrderQuery = noOrderQuery.eq('branch_id', branchId);
        }
        if (opts) noOrderQuery = noOrderQuery.range(offset, offset + limit - 1);
        
        const noOrderResult = await noOrderQuery;
        if (noOrderResult.error) throw noOrderResult.error;
        simpleData = noOrderResult.data;
        simpleError = noOrderResult.error;
        simpleCount = noOrderResult.count;
      }
      
      if (simpleError) throw simpleError;
      if (simpleData?.length) {
        await enrichPurchasesWithCreatorNames(simpleData);
        await enrichPurchasesWithSupplierContacts(simpleData);
        await enrichPurchasesWithBranchNames(simpleData);
        await enrichPurchasesWithItemCounts(simpleData);
      }
      if (opts) return { data: simpleData || [], total: simpleCount ?? 0 };
      return simpleData;
    }
    
    if (error) throw error;

    await enrichPurchasesWithCreatorNames(data || []);
    // Embed may be empty/missing while headers exist — fill branch name + line counts
    if (data?.length) {
      const missingBranch = data.some((p: any) => p.branch_id && !p.branch?.name);
      const missingItems = data.some(
        (p: any) => !Array.isArray(p.items) || p.items.length === 0,
      );
      if (missingBranch) await enrichPurchasesWithBranchNames(data);
      if (missingItems) await enrichPurchasesWithItemCounts(data);
    }

    // 🔒 LOCK CHECK: Add hasReturn and returnCount to each purchase
    if (data && data.length > 0) {
      const purchaseIds = data.map((p: any) => p.id);
      const { data: allReturns } = await supabase
        .from('purchase_returns')
        .select('original_purchase_id')
        .in('original_purchase_id', purchaseIds)
        .eq('status', 'final');
      
      const returnsMap = new Map<string, number>();
      (allReturns || []).forEach((r: any) => {
        const count = returnsMap.get(r.original_purchase_id) || 0;
        returnsMap.set(r.original_purchase_id, count + 1);
      });
      
      data.forEach((purchase: any) => {
        purchase.hasReturn = returnsMap.has(purchase.id);
        purchase.returnCount = returnsMap.get(purchase.id) || 0;
      });
    }

    if (opts) return { data: data || [], total: count ?? 0 };
    return data;
  },

  // Get single purchase (include attachments + purchase_charges for line-level extra expenses on edit)
  // Use explicit column lists for product/variation to avoid requesting current_stock (column may not exist).
  /** When embeds fail (PGRST204/400) or `*, attachments` duplicate broke PostgREST, load header + related rows separately. */
  async getPurchaseSplit(id: string): Promise<any> {
    const [{ data: header, error: hErr }, attRes] = await Promise.all([
      supabase.from('purchases').select(PURCHASE_HEADER_COLUMNS).eq('id', id).single(),
      supabase.from('purchases').select('attachments').eq('id', id).maybeSingle(),
    ]);
    if (hErr) throw hErr;
    const attachments =
      !attRes.error && attRes.data ? ((attRes.data as { attachments?: unknown }).attachments ?? null) : null;

    const supplierId = (header as any)?.supplier_id as string | null | undefined;
    const [{ data: itemsRaw }, chargesRes, supplierRes] = await Promise.all([
      supabase.from('purchase_items').select('*').eq('purchase_id', id),
      supabase.from('purchase_charges').select('*').eq('purchase_id', id),
      supplierId
        ? supabase.from('contacts').select('id, name, phone').eq('id', supplierId).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ]);
    const charges = chargesRes.error ? [] : chargesRes.data || [];

    const itemsList = itemsRaw || [];
    const productIds = [...new Set(itemsList.map((r: any) => r.product_id).filter(Boolean))] as string[];
    const variationIds = [...new Set(itemsList.map((r: any) => r.variation_id).filter(Boolean))] as string[];

    let productsById: Record<string, any> = {};
    if (productIds.length > 0) {
      const prodsRes = await supabase
        .from('products')
        .select('id, name, sku, cost_price, has_variations, unit_id, category_id, min_stock, max_stock')
        .in('id', productIds);
      if (!prodsRes.error && prodsRes.data) {
        (prodsRes.data as any[]).forEach((p: any) => {
          if (p?.id) productsById[p.id] = p;
        });
      } else {
        const minimal = await supabase.from('products').select('id, name, sku').in('id', productIds);
        (minimal.data || []).forEach((p: any) => {
          if (p?.id) productsById[p.id] = p;
        });
      }
    }

    let varsById: Record<string, any> = {};
    if (variationIds.length > 0) {
      const { data: vars } = await supabase
        .from('product_variations')
        .select('id, product_id, sku, attributes')
        .in('id', variationIds);
      (vars || []).forEach((v: any) => {
        if (v?.id) varsById[v.id] = v;
      });
    }

    const items = itemsList.map((row: any) => ({
      ...row,
      product: row.product_id ? productsById[row.product_id] ?? null : null,
      variation: row.variation_id ? varsById[row.variation_id] ?? null : null,
    }));

    const data: any = {
      ...(header as any),
      attachments,
      supplier: supplierRes?.data ?? null,
      items,
      purchase_charges: charges || [],
    };

    const { data: returns } = await supabase
      .from('purchase_returns')
      .select('id')
      .eq('original_purchase_id', id)
      .eq('status', 'final')
      .limit(1);

    data.hasReturn = (returns && returns.length > 0) || false;
    data.returnCount = returns?.length || 0;
    data.charges = Array.isArray(data.purchase_charges) ? data.purchase_charges : [];

    return data;
  },

  async getPurchase(id: string) {
    // NOTE: Do not list `attachments` after `*` — duplicate field in select often yields PostgREST 400 (`select=*` requests).
    const embeddedSelect = `
        *,
        supplier:contacts!purchases_supplier_id_fkey(id, name, phone),
        items:purchase_items(
          *,
          product:products(id, name, sku, cost_price, has_variations, unit_id, category_id, min_stock, max_stock),
          variation:product_variations(id, product_id, sku, attributes)
        ),
        purchase_charges(*)
      `;

    const { data, error } = await supabase.from('purchases').select(embeddedSelect).eq('id', id).single();

    if (error) {
      const code = (error as any).code;
      const msg = String((error as any).message || '');
      const retry =
        code === 'PGRST200' ||
        code === 'PGRST201' ||
        code === 'PGRST204' ||
        code === '42703' ||
        (error as any).status === 400 ||
        msg.toLowerCase().includes('column') ||
        msg.toLowerCase().includes('relationship');
      console.warn('[PURCHASE SERVICE] getPurchase embedded failed; retrying split fetch:', error);
      if (retry) {
        return this.getPurchaseSplit(id);
      }
      throw error;
    }

    // 🔒 LOCK CHECK: Check if purchase has returns (prevents editing)
    const { data: returns } = await supabase
      .from('purchase_returns')
      .select('id')
      .eq('original_purchase_id', id)
      .eq('status', 'final')
      .limit(1);

    if (data) {
      (data as any).hasReturn = (returns && returns.length > 0) || false;
      (data as any).returnCount = returns?.length || 0;
      (data as any).charges = Array.isArray((data as any).purchase_charges) ? (data as any).purchase_charges : [];
    }

    return data;
  },

  /** Replace all purchase_charges for a purchase (delete existing + insert). Used on edit to persist line-level expenses. */
  async replacePurchaseCharges(purchaseId: string, charges: PurchaseChargeInsert[], createdBy?: string | null) {
    const { error: delError } = await supabase
      .from('purchase_charges')
      .delete()
      .eq('purchase_id', purchaseId);
    if (delError) {
      console.warn('[PURCHASE SERVICE] replacePurchaseCharges delete failed:', delError);
      throw delError;
    }
    const chargeRows = (charges || [])
      .filter((c) => c.amount > 0)
      .map((c) => ({
        purchase_id: purchaseId,
        charge_type: c.charge_type,
        ledger_account_id: c.ledger_account_id ?? null,
        amount: Number(c.amount),
        created_by: createdBy ?? null,
      }));
    if (chargeRows.length > 0) {
      const { error: insError } = await supabase.from('purchase_charges').insert(chargeRows);
      if (insError) {
        console.warn('[PURCHASE SERVICE] replacePurchaseCharges insert failed:', insError);
        throw insError;
      }
    }
  },

  /** Cancel purchase (final/received): reverses stock, sets status=cancelled. */
  async cancelPurchase(id: string, _options?: { reason?: string; performedBy?: string }) {
    await this.updatePurchaseStatus(id, 'cancelled');
  },

  // Update purchase status (when 'cancelled': create PURCHASE_CANCELLED stock reversals, then update status)
  async updatePurchaseStatus(id: string, status: Purchase['status']) {
    const auditPurchaseRow = (
      row: Record<string, unknown> | null | undefined,
      previousStatus: string | undefined,
      auditAction: AuditAction
    ) => {
      const cid = row?.company_id as string | undefined;
      const pid = row?.id as string | undefined;
      if (!cid || !pid) return;
      logPurchaseAuditNonBlocking(cid, pid, auditAction, {
        status: row?.status,
        po_no: row?.po_no,
        previousStatus,
      });
    };

    if (status === 'cancelled') {
      const { data: purchaseRow } = await supabase.from('purchases').select('id, po_no, branch_id, company_id, status').eq('id', id).single();
      if (!purchaseRow) throw new Error('Purchase not found');
      const poNo = (purchaseRow as any).po_no || `PUR-${id.substring(0, 8)}`;
      const priorPosted = wasPurchasePostedForReversal((purchaseRow as any).status);

      // Draft / ordered: no stock reversal (inventory was never posted for this PO)
      if (!priorPosted) {
        const { data, error } = await supabase
          .from('purchases')
          .update({ status })
          .eq('id', id)
          .select(PURCHASE_HEADER_COLUMNS)
          .single();
        if (error) throw error;
        auditPurchaseRow(data as Record<string, unknown>, (purchaseRow as any).status, 'cancelled');
        return data;
      }

      const { data: existingReversal } = await supabase
        .from('stock_movements')
        .select('id')
        .eq('reference_type', 'purchase')
        .eq('reference_id', id)
        .eq('movement_type', 'PURCHASE_CANCELLED')
        .limit(1);
      if (existingReversal && existingReversal.length > 0) {
        const { data, error } = await supabase
          .from('purchases')
          .update({ status })
          .eq('id', id)
          .select(PURCHASE_HEADER_COLUMNS)
          .single();
        if (error) throw error;
        reversePurchaseDocumentAccounting(id).catch((err: any) =>
          console.warn('[purchaseService] Document accounting reversal failed (non-critical):', err?.message)
        );
        auditPurchaseRow(data as Record<string, unknown>, (purchaseRow as any).status, 'cancelled');
        return data;
      }

      const { data: stockMovements } = await supabase
        .from('stock_movements')
        .select('id, company_id, branch_id, product_id, variation_id, quantity, unit_cost, total_cost, box_change, piece_change')
        .eq('reference_type', 'purchase')
        .eq('reference_id', id)
        .eq('movement_type', 'purchase');

      if (stockMovements && stockMovements.length > 0) {
        for (const m of stockMovements) {
          const reverseMovement: Record<string, unknown> = {
            company_id: m.company_id,
            branch_id: m.branch_id,
            product_id: m.product_id,
            variation_id: m.variation_id ?? null,
            movement_type: 'PURCHASE_CANCELLED',
            quantity: -(Number(m.quantity) || 0),
            unit_cost: Number(m.unit_cost) || 0,
            total_cost: -(Number(m.total_cost) || 0),
            reference_type: 'purchase',
            reference_id: id,
            notes: `Reversal of ${poNo} (Cancelled)`,
          };
          if (m.box_change != null) (reverseMovement as any).box_change = -(Number(m.box_change) || 0);
          if (m.piece_change != null) (reverseMovement as any).piece_change = -(Number(m.piece_change) || 0);
          const { error: insertErr } = await supabase.from('stock_movements').insert(reverseMovement);
          if (insertErr) throw insertErr;
        }
      }

      const { data, error } = await supabase
        .from('purchases')
        .update({ status })
        .eq('id', id)
        .select(PURCHASE_HEADER_COLUMNS)
        .single();
      if (error) throw error;
      reversePurchaseDocumentAccounting(id).catch((err: any) =>
        console.warn('[purchaseService] Document accounting reversal failed (non-critical):', err?.message)
      );
      auditPurchaseRow(data as Record<string, unknown>, (purchaseRow as any).status, 'cancelled');
      return data;
    }

    const { data: priorRow } = await supabase.from('purchases').select('status').eq('id', id).maybeSingle();
    const prevStatus = (priorRow as { status?: string } | null)?.status;
    if (String(prevStatus).toLowerCase() === 'cancelled') {
      throw new Error(
        'Cannot change status from cancelled. Use Restore to Draft or Order from the purchases list first.'
      );
    }

    let { data, error } = await supabase
      .from('purchases')
      .update({ status })
      .eq('id', id)
      .select(PURCHASE_HEADER_COLUMNS)
      .single();

    // Auto-fix: if update failed and we sent 'final', try all common enum casings (DB may use final/FINAL/Final).
    if (error && status === 'final') {
      const variants: Array<'final' | 'FINAL' | 'Final'> = ['final', 'FINAL', 'Final'];
      for (const value of variants) {
        let { data: retryData, error: retryError } = await supabase
          .from('purchases')
          .update({ status: value })
          .eq('id', id)
          .select(PURCHASE_HEADER_COLUMNS)
          .single();
        if (!retryError && retryData) {
          const newSt = (retryData as { status?: string }).status;
          if (canPostAccountingForPurchaseStatus(newSt) && !canPostAccountingForPurchaseStatus(prevStatus)) {
            try {
              retryData = (await ensurePostedPurchasePoNoAllocated(
                id,
                retryData as Record<string, unknown>
              )) as typeof retryData;
            } catch (allocErr: unknown) {
              const msg = allocErr instanceof Error ? allocErr.message : String(allocErr);
              await supabase
                .from('purchases')
                .update({ status: (prevStatus as Purchase['status'] | undefined) ?? 'draft' })
                .eq('id', id);
              throw new Error(msg);
            }
            postPurchaseDocumentAccounting(id).catch((err: any) =>
              console.warn('[purchaseService] Document posting engine failed (non-critical):', err?.message)
            );
          }
          auditPurchaseRow(retryData as Record<string, unknown>, prevStatus, 'updated');
          return retryData;
        }
      }
    }
    if (error) throw error;
    const newSt = (data as { status?: string }).status ?? status;
    if (canPostAccountingForPurchaseStatus(newSt) && !canPostAccountingForPurchaseStatus(prevStatus)) {
      try {
        data = (await ensurePostedPurchasePoNoAllocated(id, data as Record<string, unknown>)) as typeof data;
      } catch (allocErr: unknown) {
        const msg = allocErr instanceof Error ? allocErr.message : String(allocErr);
        await supabase
          .from('purchases')
          .update({ status: (prevStatus as Purchase['status'] | undefined) ?? 'draft' })
          .eq('id', id);
        throw new Error(msg);
      }
      postPurchaseDocumentAccounting(id).catch((err: any) =>
        console.warn('[purchaseService] Document posting engine failed (non-critical):', err?.message)
      );
    }
    auditPurchaseRow(data as Record<string, unknown>, prevStatus, 'updated');
    return data;
  },

  /**
   * QA / repair: if status is received/final but po_no is empty or still PDR/POR, allocate PUR and persist.
   * Does not change status or re-run document posting.
   */
  async repairMissingPostedPurchasePoNo(id: string) {
    const { data: row, error } = await supabase.from('purchases').select('*').eq('id', id).maybeSingle();
    if (error || !row) throw new Error('Purchase not found');
    if (!canPostAccountingForPurchaseStatus((row as { status?: string }).status)) {
      throw new Error('Repair PO number only applies to received/final (posted) purchases.');
    }
    if (!purchaseRowNeedsPostedPoAllocation(row as Record<string, unknown>)) {
      return row;
    }
    return ensurePostedPurchasePoNoAllocated(id, row as Record<string, unknown>);
  },

  /**
   * Move a cancelled PO back to draft or ordered (unposted). Historical reversals remain for audit.
   */
  async restoreCancelledPurchase(id: string, target: 'draft' | 'ordered', companyId: string) {
    const { data: row, error: fetchErr } = await supabase
      .from('purchases')
      .select('*')
      .eq('id', id)
      .single();
    if (fetchErr || !row) throw new Error('Purchase not found');
    if (String((row as { status?: string }).status).toLowerCase() !== 'cancelled') {
      throw new Error('Only cancelled purchase orders can be restored.');
    }

    const r = row as Record<string, unknown>;
    const patch: Record<string, unknown> = {
      status: target,
      po_no: null,
    };
    if (target === 'draft' && !(r.draft_no && String(r.draft_no).trim())) {
      patch.draft_no = await documentNumberService.getNextDocumentNumberGlobal(companyId, 'PDR');
    }
    if (target === 'ordered' && !(r.order_no && String(r.order_no).trim())) {
      patch.order_no = await documentNumberService.getNextDocumentNumberGlobal(companyId, 'POR');
    }

    const { error } = await supabase.from('purchases').update(patch).eq('id', id).eq('status', 'cancelled');
    if (error) throw error;
    logPurchaseAuditNonBlocking(companyId, id, 'restored', { target, previousStatus: 'cancelled' });
  },

  // Update purchase. Only DB columns (discount_amount, no discount_percentage). Map app 'discount' → discount_amount.
  async updatePurchase(id: string, updates: Partial<Purchase>) {
    // 🔒 CANCELLED: No updates allowed on cancelled purchases
    const { data: existingPurchase } = await supabase.from('purchases').select('status').eq('id', id).single();
    if (existingPurchase && (existingPurchase as any).status === 'cancelled') {
      throw new Error('Cannot edit a cancelled purchase order.');
    }
    const raw = updates as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};
    const allowed = new Set(PURCHASE_INSERT_KEYS);
    for (const key of Object.keys(raw)) {
      if (key === 'discount' && raw.discount !== undefined) {
        sanitized.discount_amount = Number(raw.discount) || 0;
      } else if (key === 'purchaseNo' && raw.purchaseNo !== undefined) {
        sanitized.po_no = raw.purchaseNo;
      } else if (allowed.has(key as any)) {
        sanitized[key] = raw[key];
      }
    }

    const { data, error } = await supabase
      .from('purchases')
      .update(sanitized)
      .eq('id', id)
      .select(PURCHASE_HEADER_COLUMNS)
      .single();

    if (error) throw error;

    if ((data as any)?.company_id) dispatchContactBalancesRefresh(String((data as any).company_id));

    if (Object.prototype.hasOwnProperty.call(sanitized, 'po_date') && sanitized.po_date != null && data) {
      const cid = (data as any).company_id;
      if (cid) {
        syncJournalEntryDateByDocumentRefs({
          companyId: cid,
          referenceTypes: ['purchase', 'purchase_adjustment'],
          referenceId: id,
          entryDate: String(sanitized.po_date),
        }).catch((e) => console.warn('[purchaseService] journal entry_date sync:', e));
      }
    }

    const d = data as { company_id?: string; po_no?: unknown; status?: unknown } | null;
    if (d?.company_id) {
      logPurchaseAuditNonBlocking(String(d.company_id), id, 'updated', {
        keys: Object.keys(sanitized),
        po_no: d.po_no,
        status: d.status,
      });
    }
    return data;
  },

  async recomputeWeightedCostsForItems(args: {
    companyId: string;
    branchId?: string | null;
    purchaseId?: string;
    items: Array<{ product_id: string; variation_id?: string | null; quantity: number; unit_price: number }>;
  }) {
    await applyWeightedCostRollup(args);
  },

  // Delete purchase with complete cascade delete
  // CRITICAL: This deletes ALL related data in correct order
  // Order: Payments → Journal Entries → Stock Movements → Ledger Entries → Activity Logs → Purchase Items → Purchase
  async deletePurchase(id: string) {
    // 🔒 CANCELLED: No delete allowed on cancelled purchases
    const { data: existingPurchase } = await supabase
      .from('purchases')
      .select('status, company_id, po_no')
      .eq('id', id)
      .single();
    if (existingPurchase && (existingPurchase as any).status === 'cancelled') {
      throw new Error('Cannot delete a cancelled purchase order.');
    }
    const ep = existingPurchase as { company_id?: string; po_no?: unknown } | null;
    if (ep?.company_id) {
      logPurchaseAuditNonBlocking(ep.company_id, id, 'deleted', { po_no: ep.po_no });
    }
    console.log('[PURCHASE SERVICE] Starting cascade delete for purchase:', id);
    
    try {
      // STEP 1: Delete all payments for this purchase (and their journal entries)
      const { data: payments } = await supabase
        .from('payments')
        .select('id')
        .eq('reference_type', 'purchase')
        .eq('reference_id', id);

      if (payments && payments.length > 0) {
        console.log(`[PURCHASE SERVICE] Found ${payments.length} payments to delete`);
        for (const payment of payments) {
          try {
            // Delete payment using the existing deletePaymentDirect method
            await this.deletePaymentDirect(payment.id, id);
          } catch (paymentError: any) {
            console.error(`[PURCHASE SERVICE] Error deleting payment ${payment.id}:`, paymentError);
            // Continue with other deletions even if one payment fails
          }
        }
      }

      // STEP 2: Delete stock movements (to reverse stock)
      // CRITICAL: Include variation_id for proper stock reversal
      const { data: stockMovements } = await supabase
        .from('stock_movements')
        .select('id, company_id, branch_id, product_id, variation_id, quantity, unit_cost, total_cost, box_change, piece_change')
        .eq('reference_type', 'purchase')
        .eq('reference_id', id);

      if (stockMovements && stockMovements.length > 0) {
        console.log(`[PURCHASE SERVICE] Found ${stockMovements.length} stock movements to reverse`);
        // Create reverse stock movements before deleting (STEP 3: Reverse, not hide)
        for (const movement of stockMovements) {
          try {
            // Create reverse movement (negative quantity to reverse stock)
            // CRITICAL: Include variation_id for variation-specific stock reversal
            const reverseMovement: Record<string, unknown> = {
              company_id: movement.company_id,
              branch_id: movement.branch_id,
              product_id: movement.product_id,
              variation_id: movement.variation_id || null,
              movement_type: 'adjustment',
              quantity: -(Number(movement.quantity) || 0),
              unit_cost: Number(movement.unit_cost) || 0,
              total_cost: -(Number(movement.total_cost) || 0),
              reference_type: 'purchase',
              reference_id: id,
              notes: `Reverse stock from deleted purchase ${id}`,
            };
            if (movement.box_change != null) reverseMovement.box_change = -(Number(movement.box_change) || 0);
            if (movement.piece_change != null) reverseMovement.piece_change = -(Number(movement.piece_change) || 0);
            
            console.log('[PURCHASE SERVICE] Creating reverse stock movement:', {
              product_id: reverseMovement.product_id,
              variation_id: reverseMovement.variation_id,
              quantity: reverseMovement.quantity
            });
            
            const { data: reverseData, error: reverseError } = await supabase
              .from('stock_movements')
              .insert(reverseMovement)
              .select()
              .single();
            
            if (reverseError) {
              console.error('[PURCHASE SERVICE] ❌ Failed to create reverse stock movement:', reverseError);
              throw reverseError; // Don't allow silent failure
            }
            
            console.log('[PURCHASE SERVICE] ✅ Reverse stock movement created:', reverseData?.id);
          } catch (reverseError: any) {
            console.error('[PURCHASE SERVICE] ❌ CRITICAL: Could not create reverse stock movement:', reverseError);
            throw new Error(`Failed to reverse stock movement: ${reverseError.message || reverseError}`);
          }
        }
        
        // Delete original stock movements
        const { error: stockError } = await supabase
          .from('stock_movements')
          .delete()
          .eq('reference_type', 'purchase')
          .eq('reference_id', id);

        if (stockError) {
          console.error('[PURCHASE SERVICE] Error deleting stock movements:', stockError);
          // Continue with other deletions
        }
      }

      // STEP 3: Delete journal entries directly linked to purchase (supplier statement = purchases + payments; no duplicate subledger)
      const { data: journalEntries } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('reference_type', 'purchase')
        .eq('reference_id', id);

      if (journalEntries && journalEntries.length > 0) {
        console.log(`[PURCHASE SERVICE] Found ${journalEntries.length} journal entries to delete`);
        for (const entry of journalEntries) {
          // Delete journal entry lines first
          const { error: lineError } = await supabase
            .from('journal_entry_lines')
            .delete()
            .eq('journal_entry_id', entry.id);

          if (lineError) {
            console.error('[PURCHASE SERVICE] Error deleting journal entry lines:', lineError);
          }

          // Then delete journal entry
          const { error: entryError } = await supabase
            .from('journal_entries')
            .delete()
            .eq('id', entry.id);

          if (entryError) {
            console.error('[PURCHASE SERVICE] Error deleting journal entry:', entryError);
          }
        }
      }

      // STEP 5: Delete activity logs
      const { error: activityError } = await supabase
        .from('activity_logs')
        .delete()
        .eq('module', 'purchase')
        .eq('entity_id', id);

      if (activityError) {
        console.warn('[PURCHASE SERVICE] Error deleting activity logs (non-critical):', activityError);
        // Activity logs deletion failure is non-critical
      }

      // STEP 6: Delete purchase items (cascade should handle this, but explicit for safety)
      const { error: itemsError } = await supabase
        .from('purchase_items')
        .delete()
        .eq('purchase_id', id);

      if (itemsError) {
        console.error('[PURCHASE SERVICE] Error deleting purchase items:', itemsError);
        // Continue - purchase deletion will cascade
      }

      // STEP 7: Finally delete the purchase record itself
      const { error: purchaseError } = await supabase
        .from('purchases')
        .delete()
        .eq('id', id);

      if (purchaseError) {
        console.error('[PURCHASE SERVICE] Error deleting purchase:', purchaseError);
        throw purchaseError;
      }

      console.log('[PURCHASE SERVICE] ✅ Cascade delete completed successfully for purchase:', id);
    } catch (error: any) {
      console.error('[PURCHASE SERVICE] ❌ Cascade delete failed for purchase:', id, error);
      throw new Error(`Failed to delete purchase: ${error.message || 'Unknown error'}`);
    }
  },

  // Record payment – allowed only when purchase status is final or received (DB enum — no `completed`).
  // Uses canonical supplierPaymentService: one payments row + one journal entry (no duplicate JE).
  async recordPayment(purchaseId: string, amount: number, paymentMethod: string, accountId: string, companyId: string, branchId?: string | null, referenceNumber?: string | null, options?: { notes?: string; attachments?: any }) {
    const maxAttempts = 3;
    let purchase: { id: string; status?: string; branch_id?: string | null } | null = null;
    let lastFetchError: unknown = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const { data, error } = await supabase
        .from('purchases')
        .select('id, status, branch_id')
        .eq('id', purchaseId)
        .maybeSingle();

      lastFetchError = error;
      if (!error && data) {
        purchase = data as typeof purchase;
        break;
      }
      if (error && isTransientSupabaseHttpError(error) && attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 350 * attempt));
        continue;
      }
      purchase = (data as typeof purchase) ?? null;
      break;
    }

    if (lastFetchError && !purchase) {
      if (isTransientSupabaseHttpError(lastFetchError)) {
        throw new Error(
          'Could not reach the database (server error). Payment was not saved — wait a moment and try again.'
        );
      }
      const msg = (lastFetchError as { message?: string })?.message;
      throw new Error(msg?.trim() ? msg : 'Could not load purchase');
    }
    if (!purchase) {
      throw new Error('Purchase not found');
    }
    const status = (purchase as any).status;
    if (status === 'cancelled') {
      throw new Error('Cannot record payment on a cancelled purchase order.');
    }
    if (!canPostAccountingForPurchaseStatus(status)) {
      throw new Error('Payment not allowed until purchase is Received or Final. Current status: ' + (status || 'unknown'));
    }
    const purchaseBranchId = (purchase as any).branch_id;
    const validBranchId = (purchaseBranchId && purchaseBranchId !== 'all') ? purchaseBranchId : (branchId && branchId !== 'all') ? branchId : null;
    if (!accountId) {
      throw new Error('Payment account is required. Please select an account.');
    }

    const result = await createSupplierPayment({
      companyId,
      branchId: validBranchId,
      amount,
      paymentMethod,
      paymentAccountId: accountId,
      purchaseId,
      paymentDate: localNowDateString(),
      notes: buildPaymentNote(options?.notes, referenceNumber),
      attachments: options?.attachments,
    });

    activityLogService.logActivity({
      companyId,
      module: 'purchase',
      entityId: purchaseId,
      action: 'payment_added',
      amount,
      paymentMethod: paymentMethod as string,
      description: `Payment of Rs ${Number(amount).toLocaleString()} recorded for purchase`,
    }).catch((err) => console.warn('[PURCHASE SERVICE] Activity log failed:', err));

    return { id: result.paymentId, reference_number: result.referenceNumber };
  },

  /**
   * Record on-account payment (direct supplier payment without bill).
   * Uses canonical supplierPaymentService: one payments row + one journal entry (Dr AP, Cr Cash/Bank).
   */
  async recordOnAccountPayment(
    contactId: string,
    contactName: string,
    amount: number,
    paymentMethod: string,
    accountId: string,
    companyId: string,
    branchId: string,
    paymentDate?: string,
    options?: { notes?: string; attachments?: any }
  ) {
    if (!accountId || !companyId || !branchId) {
      throw new Error('Account, company and branch are required for on-account payment.');
    }
    const validBranchId = (branchId && branchId !== 'all') ? branchId : null;
    const result = await createSupplierPayment({
      companyId,
      branchId: validBranchId,
      amount,
      paymentMethod,
      paymentAccountId: accountId,
      contactId,
      supplierName: contactName,
      paymentDate: paymentDate || localNowDateString(),
      notes: buildPaymentNote(options?.notes, null),
      attachments: options?.attachments,
    });
    return { id: result.paymentId, reference_number: result.referenceNumber };
  },

  // Update payment (Phase 3: isolated — post only payment delta JEs, never touch document JEs)
  async updatePayment(
    paymentId: string,
    purchaseId: string,
    updates: {
      amount?: number;
      paymentMethod?: string;
      accountId?: string;
      paymentDate?: string;
      /** When set, updates payments.created_at (business event time for roznamcha / picker). */
      eventTimestamp?: string;
      referenceNumber?: string;
      notes?: string;
      attachments?: any;
    }
  ) {
    try {
      const { tracePaymentEditFlow } = await import('@/app/lib/paymentEditFlowTrace');
      tracePaymentEditFlow('purchaseService.updatePayment.start', {
        paymentId,
        purchaseId,
        updatesKeys: Object.keys(updates),
      });
      // Phase 3: Capture current payment before update (for amount/account adjustment JEs)
      let oldAmount: number | null = null;
      let oldAccountId: string | null = null;
      let paymentAccountId: string | null = null;
      let companyId: string | null = null;
      let branchId: string | null = null;
      let paymentDate: string | null = null;
      const needPreState = updates.amount !== undefined || updates.accountId !== undefined || updates.paymentMethod !== undefined;
      if (needPreState) {
        const { data: current } = await supabase
          .from('payments')
          .select('amount, payment_account_id, company_id, branch_id, payment_date')
          .eq('id', paymentId)
          .single();
        if (current) {
          const c = current as any;
          oldAmount = Number(c.amount ?? 0) || 0;
          oldAccountId = c.payment_account_id ?? null;
          paymentAccountId = c.payment_account_id ?? null;
          companyId = c.company_id ?? null;
          branchId = c.branch_id ?? null;
          paymentDate = c.payment_date ?? null;
        }
      }

      // Normalize payment method if provided
      let normalizedPaymentMethod = updates.paymentMethod;
      if (updates.paymentMethod) {
        const normalized = updates.paymentMethod.toLowerCase().trim();
        const paymentMethodMap: Record<string, string> = {
          'cash': 'cash',
          'Cash': 'cash',
          'bank': 'bank',
          'Bank': 'bank',
          'card': 'card',
          'Card': 'card',
          'cheque': 'other',
          'Cheque': 'other',
          'mobile wallet': 'other',
          'Mobile Wallet': 'other',
          'mobile_wallet': 'other',
          'wallet': 'other',
          'Wallet': 'other',
        };
        normalizedPaymentMethod = paymentMethodMap[updates.paymentMethod] || paymentMethodMap[normalized] || 'cash';
      }

      // Build update data
      const updateData: any = {};
      if (updates.amount !== undefined) updateData.amount = updates.amount;
      if (normalizedPaymentMethod) updateData.payment_method = normalizedPaymentMethod;
      if (updates.accountId) updateData.payment_account_id = updates.accountId;
      if (updates.paymentDate) updateData.payment_date = updates.paymentDate;
      if (updates.eventTimestamp) updateData.created_at = updates.eventTimestamp;
      if (updates.referenceNumber !== undefined) updateData.reference_number = updates.referenceNumber;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.attachments !== undefined) updateData.attachments = updates.attachments;

      const { data, error } = await supabase
        .from('payments')
        .update(updateData)
        .eq('id', paymentId)
        .select()
        .single();

      if (error) {
        console.error('[PURCHASE SERVICE] Error updating payment:', error);
        throw error;
      }

      const newAmount = updates.amount !== undefined ? Number(updates.amount) : oldAmount;
      tracePaymentEditFlow('purchaseService.updatePayment.db_updated', {
        paymentId,
        purchaseId,
        oldAmount,
        oldAccountId,
        newAmount,
        newAccountId: (data as any)?.payment_account_id ?? updates.accountId ?? null,
      });

      // Phase 3: Post payment amount adjustment JE when amount changed (original payment JE stays untouched)
      if (
        oldAmount != null &&
        newAmount != null &&
        oldAmount !== newAmount &&
        companyId &&
        paymentAccountId
      ) {
        try {
          const purchaseRow = await this.getPurchase(purchaseId).catch(() => null);
          const poNo = (purchaseRow as any)?.po_no ?? `PUR-${purchaseId.substring(0, 8)}`;
          let payableAccountId: string | undefined;
          if (companyId && purchaseRow && (purchaseRow as any).supplier_id) {
            const { resolvePayablePostingAccountId } = await import('@/app/services/partySubledgerAccountService');
            payableAccountId =
              (await resolvePayablePostingAccountId(companyId, String((purchaseRow as any).supplier_id))) || undefined;
          }
          // In-place update: modify existing JE lines directly instead of creating adjustment JE
          tracePaymentEditFlow('purchaseService.updatePayment.post_amount_adjust', {
            paymentId, purchaseId, oldAmount, newAmount,
            deltaLiquidityAccountId: oldAccountId || paymentAccountId,
          });
          // Find the existing payment JE and update its line amounts
          const { data: existingJe } = await supabase
            .from('journal_entries')
            .select('id')
            .eq('payment_id', paymentId)
            .or('is_void.is.null,is_void.eq.false')
            .not('reference_type', 'eq', 'correction_reversal')
            .not('reference_type', 'eq', 'payment_adjustment')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          if (existingJe?.id) {
            // Update all debit lines to new amount
            await supabase.from('journal_entry_lines').update({ debit: newAmount }).eq('journal_entry_id', existingJe.id).gt('debit', 0);
            // Update all credit lines to new amount
            await supabase.from('journal_entry_lines').update({ credit: newAmount }).eq('journal_entry_id', existingJe.id).gt('credit', 0);
            // Log edit in description
            const oldDesc = (await supabase.from('journal_entries').select('description').eq('id', existingJe.id).maybeSingle())?.data?.description || '';
            const ts = new Date().toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' });
            await supabase.from('journal_entries').update({
              description: `${oldDesc} [Edited ${ts}: Rs ${oldAmount.toLocaleString()} → Rs ${newAmount.toLocaleString()}]`.slice(0, 500)
            }).eq('id', existingJe.id);
            // Patch to supplier sub-ledger if needed
            const { patchPaymentJeToSubLedger } = await import('@/app/services/saleAccountingService');
            await patchPaymentJeToSubLedger(paymentId, existingJe.id);
          } else {
            // Fallback: create adjustment JE if no existing JE found
            const { postPaymentAmountAdjustment } = await import('@/app/services/paymentAdjustmentService');
            const { data: { user } } = await supabase.auth.getUser();
            await postPaymentAmountAdjustment({
              context: 'purchase', companyId, branchId, paymentId, referenceId: purchaseId,
              oldAmount, newAmount, paymentAccountId: oldAccountId || paymentAccountId || '',
              invoiceNoOrRef: poNo,
              entryDate: (updates.paymentDate || paymentDate || localNowDateString()).toString().slice(0, 10),
              createdBy: (user as any)?.id ?? null, payableAccountId,
            });
          }
          if ((data as any)?.company_id) {
            dispatchAccountingInvalidated({
              companyId: String((data as any).company_id),
              entityId: paymentId,
              reason: 'purchasePaymentAmountAdjustmentJe',
            });
          }
        } catch (adjErr: any) {
          console.warn('[PURCHASE SERVICE] Payment amount adjustment JE failed:', adjErr?.message || adjErr);
        }
      }

      // Phase 3: When payment account/method changed, update existing JE line's account_id in-place
      const newAccountId = (data as any)?.payment_account_id ?? updates.accountId ?? null;
      if (
        companyId &&
        oldAccountId != null &&
        newAccountId != null &&
        oldAccountId !== newAccountId &&
        (newAmount != null && newAmount > 0)
      ) {
        try {
          tracePaymentEditFlow('purchaseService.updatePayment.post_account_adjust', {
            paymentId,
            purchaseId,
            oldAccountId,
            newAccountId,
            amount: newAmount,
          });
          // Find existing primary JE for this payment
          const { data: existingJe } = await supabase
            .from('journal_entries')
            .select('id')
            .eq('payment_id', paymentId)
            .or('is_void.is.null,is_void.eq.false')
            .not('reference_type', 'eq', 'correction_reversal')
            .not('reference_type', 'eq', 'payment_adjustment')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (existingJe?.id) {
            // In-place: update the liquidity line's account_id (the line matching oldAccountId)
            await supabase
              .from('journal_entry_lines')
              .update({ account_id: newAccountId })
              .eq('journal_entry_id', existingJe.id)
              .eq('account_id', oldAccountId);
            // Log the account change in JE description
            const oldDesc = (await supabase.from('journal_entries').select('description').eq('id', existingJe.id).maybeSingle())?.data?.description || '';
            const ts = new Date().toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' });
            await supabase.from('journal_entries').update({
              description: `${oldDesc} [Acct changed ${ts}: ${oldAccountId.slice(0, 8)} → ${newAccountId.slice(0, 8)}]`.slice(0, 500)
            }).eq('id', existingJe.id);
          } else {
            // Fallback: create adjustment JE if no existing JE found (legacy behavior)
            const purchaseRow = await this.getPurchase(purchaseId).catch(() => null);
            const poNo = (purchaseRow as any)?.po_no ?? `PUR-${purchaseId.substring(0, 8)}`;
            const { postPaymentAccountAdjustment } = await import('@/app/services/paymentAdjustmentService');
            const { data: { user } } = await supabase.auth.getUser();
            await postPaymentAccountAdjustment({
              context: 'purchase', companyId, branchId, paymentId, referenceId: purchaseId,
              oldAccountId, newAccountId, amount: newAmount, invoiceNoOrRef: poNo,
              entryDate: (updates.paymentDate || paymentDate || (data as any)?.payment_date || localNowDateString()).toString().slice(0, 10),
              createdBy: (user as any)?.id ?? null,
            });
          }
          // Clear payment sync cache so next sync re-evaluates this payment
          const { clearSkippedPaymentCache } = await import('@/app/services/paymentAdjustmentService');
          clearSkippedPaymentCache(paymentId);
          if ((data as any)?.company_id) {
            dispatchAccountingInvalidated({
              companyId: String((data as any).company_id),
              entityId: paymentId,
              reason: 'purchasePaymentAccountAdjustmentJe',
            });
          }
        } catch (accErr: any) {
          console.warn('[PURCHASE SERVICE] Payment account adjustment JE failed:', accErr?.message || accErr);
        }
      }

      console.log('[PURCHASE SERVICE] Payment updated successfully');
      if (updates.paymentDate && (data as any)?.company_id) {
        syncJournalEntryDateByPaymentId({
          companyId: (data as any).company_id,
          paymentId,
          entryDate: updates.paymentDate,
        }).catch((e) => console.warn('[purchaseService] payment journal entry_date sync:', e));
      }
      if ((data as any)?.company_id) {
        dispatchContactBalancesRefresh(String((data as any).company_id));
        dispatchAccountingInvalidated({
          companyId: String((data as any).company_id),
          entityId: paymentId,
          reason: 'purchasePaymentUpdated',
        });
      }
      return data;
    } catch (error: any) {
      console.error('[PURCHASE SERVICE] Error updating payment:', error);
      throw error;
    }
  },

  // Get purchase payments: direct purchase-linked rows + manual_payment allocations (Add Entry / AP), like getSalePayments.
  async getPurchasePayments(purchaseId: string) {
    const selectWithAttachments = `
      id,
      payment_date,
      reference_number,
      amount,
      payment_method,
      payment_account_id,
      notes,
      attachments,
      created_at,
      updated_at,
      voided_at,
      account:accounts(id, name)
    `;
    let result = await supabase
      .from('payments')
      .select(selectWithAttachments)
      .eq('reference_type', 'purchase')
      .eq('reference_id', purchaseId)
      .order('payment_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (result.error && result.error.code === 'PGRST204' && result.error.message?.includes('attachments')) {
      result = await supabase
        .from('payments')
        .select(
          `
          id,
          payment_date,
          reference_number,
          amount,
          payment_method,
          payment_account_id,
          notes,
          created_at,
          updated_at,
          voided_at,
          account:accounts(id, name)
        `
        )
        .eq('reference_type', 'purchase')
        .eq('reference_id', purchaseId)
        .order('payment_date', { ascending: false })
        .order('created_at', { ascending: false });
    }

    const data = result.data;
    const error = result.error;
    if (error) {
      console.error('[PURCHASE SERVICE] Error fetching direct payments:', error);
    }

    const directRows: any[] = [];
    if (data && data.length > 0) {
      for (const p of data as any[]) {
        if (p.voided_at) continue;
        let att = p.attachments;
        if (typeof att === 'string' && att) {
          try {
            att = JSON.parse(att);
          } catch {
            att = null;
          }
        }
        directRows.push({
          id: p.id,
          date: p.payment_date || p.created_at?.split('T')[0] || localNowDateString(),
          referenceNo: p.reference_number || '',
          amount: parseFloat(p.amount || 0),
          method: p.payment_method || 'cash',
          accountId: p.payment_account_id,
          accountName: p.account?.name || '',
          notes: p.notes || '',
          attachments: att ?? null,
          createdAt: p.created_at,
          updatedAt: p.updated_at ?? p.created_at,
          source: 'purchase_payment' as const,
        });
      }
    }

    const allocRows: any[] = [];
    try {
      const { data: allocs, error: aErr } = await supabase
        .from('payment_allocations')
        .select('id, allocated_amount, allocation_date, payment_id, allocation_order')
        .eq('purchase_id', purchaseId);
      if (!aErr && allocs && allocs.length > 0) {
        const payIds = [...new Set(allocs.map((a: any) => a.payment_id).filter(Boolean))];
        const { data: parents } = await supabase
          .from('payments')
          .select(
            `id, reference_type, payment_date, reference_number, amount, payment_method, payment_account_id, notes, attachments, voided_at, created_at, updated_at, account:accounts(id, name)`
          )
          .in('id', payIds);
        const parentById = new Map((parents || []).map((p: any) => [p.id, p]));
        /** One display row per parent manual payment (sum allocations); date = parent payment_date so edits show immediately */
        const grouped = new Map<
          string,
          { payment_id: string; sum: number; primaryAllocId: string; minOrder: number; allocCount: number }
        >();
        for (const a of allocs as any[]) {
          const p = parentById.get(a.payment_id);
          if (!p || (p as any).voided_at) continue;
          if (String((p as any).reference_type || '').toLowerCase() !== 'manual_payment') continue;
          const pid = String(a.payment_id);
          const amt = parseFloat(a.allocated_amount || 0);
          const ord = Number(a.allocation_order) || 0;
          const g = grouped.get(pid);
          if (!g) {
            grouped.set(pid, {
              payment_id: pid,
              sum: amt,
              primaryAllocId: String(a.id),
              minOrder: ord,
              allocCount: 1,
            });
          } else {
            g.sum += amt;
            g.allocCount += 1;
            if (ord < g.minOrder) g.minOrder = ord;
          }
        }
        for (const [, g] of grouped) {
          const p = parentById.get(g.payment_id);
          if (!p) continue;
          const payDate = (p as any)?.payment_date || localNowDateString();
          let att = (p as any).attachments;
          if (typeof att === 'string' && att) {
            try {
              att = JSON.parse(att);
            } catch {
              att = null;
            }
          }
          allocRows.push({
            id: `alloc:${g.primaryAllocId}`,
            date: payDate,
            referenceNo: (p as any)?.reference_number || '',
            allocationBadge:
              g.allocCount > 1
                ? `AP alloc · ${g.allocCount} lines`
                : `AP alloc #${g.minOrder || 1}`,
            amount: g.sum,
            parentPaymentAmount: parseFloat((p as any)?.amount || 0),
            method: (p as any)?.payment_method || 'cash',
            accountId: (p as any)?.payment_account_id,
            accountName: (p as any)?.account?.name || '',
            notes: (p as any)?.notes || '',
            attachments: att ?? null,
            createdAt: (p as any)?.created_at,
            updatedAt: (p as any)?.updated_at ?? (p as any)?.created_at,
            source: 'manual_payment_allocation' as const,
            parentPaymentId: g.payment_id,
            allocationOrder: g.minOrder,
          });
        }
      }
    } catch (e) {
      console.warn('[PURCHASE SERVICE] payment_allocations fetch skipped:', e);
    }

    const combined = [...directRows, ...allocRows].sort((x, y) => {
      const dx = new Date(x.date).getTime();
      const dy = new Date(y.date).getTime();
      if (dy !== dx) return dy - dx;
      return String(y.referenceNo).localeCompare(String(x.referenceNo));
    });

    return combined;
  },

  // Delete payment (similar to saleService.deletePayment)
  // Tries RPC delete_payment first; if 404 or missing, uses direct deletion.
  async deletePayment(paymentId: string, purchaseId: string) {
    const tryRpc = async () => {
      const { error } = await supabase.rpc('delete_payment', {
        p_payment_id: paymentId,
        p_reference_id: purchaseId
      });
      return error;
    };

    try {
      const rpcError = await tryRpc();
      if (!rpcError) return; // RPC success
      // RPC failed (e.g. 404 function not found) → use direct delete
      return await this.deletePaymentDirect(paymentId, purchaseId);
    } catch (err: any) {
      // RPC request can throw (e.g. 404) → use direct delete
      return await this.deletePaymentDirect(paymentId, purchaseId);
    }
  },

  /** Period purchases for reports (not paginated PurchaseContext). */
  async getPurchasesForReports(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string | null
  ): Promise<{ data: any[]; total: number; truncated: boolean }> {
    const max = 5000;
    const selectAttempts = [
      `id, company_id, branch_id, po_no, draft_no, order_no, po_date, supplier_id, supplier_name,
       status, payment_status, subtotal, discount_amount, tax_amount, total, paid_amount, due_amount,
       created_at, branch:branches(id, name, code)`,
      `id, company_id, branch_id, po_no, draft_no, order_no, po_date, supplier_id, supplier_name,
       status, payment_status, subtotal, total, paid_amount, due_amount, created_at,
       branch:branches(id, name, code)`,
      `*, branch:branches(id, name, code)`,
    ];

    const runQuery = (selectFields: string, dateCol: 'po_date' | 'created_at') => {
      let q = supabase
        .from('purchases')
        .select(selectFields, { count: 'exact' })
        .eq('company_id', companyId)
        .gte(dateCol, startDate)
        .lte(dateCol, endDate)
        .order(dateCol, { ascending: false })
        .limit(max);
      if (branchId && branchId !== 'all') q = q.eq('branch_id', branchId);
      return q;
    };

    let rows: any[] = [];
    let count: number | null = null;
    let lastError: unknown = null;

    for (const fields of selectAttempts) {
      let res = await runQuery(fields, 'po_date');
      if (res.error && (res.error as { code?: string }).code === '42703') {
        res = await runQuery(fields, 'created_at');
      }
      if (!res.error) {
        rows = res.data || [];
        count = res.count;
        lastError = null;
        break;
      }
      lastError = res.error;
      console.warn('[purchaseService.getPurchasesForReports] select fallback:', fields.trim().slice(0, 80), res.error);
    }
    if (lastError) throw lastError;

    const total = count ?? rows.length;
    return { data: rows, total, truncated: total > max };
  },

  // Direct delete fallback (if RPC not available)
  async deletePaymentDirect(paymentId: string, purchaseId: string) {
    try {
      // Fetch payment details before deletion
      const { data: paymentData, error: fetchError } = await supabase
        .from('payments')
        .select('*')
        .eq('id', paymentId)
        .single();

      if (fetchError || !paymentData) {
        throw new Error('Payment not found');
      }

      // Delete related journal entries first
      const { data: journalEntries } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('payment_id', paymentId);

      if (journalEntries && journalEntries.length > 0) {
        // Delete journal entry lines first
        for (const entry of journalEntries) {
          const { error: lineError } = await supabase
            .from('journal_entry_lines')
            .delete()
            .eq('journal_entry_id', entry.id);
          
          if (lineError) {
            console.error('[PURCHASE SERVICE] Error deleting journal entry lines:', lineError);
          }
        }

        // Then delete journal entries
        const { error: entryError } = await supabase
          .from('journal_entries')
          .delete()
          .eq('payment_id', paymentId);
        
        if (entryError) {
          console.error('[PURCHASE SERVICE] Error deleting journal entries:', entryError);
        }
      }

      // Finally delete the payment
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('id', paymentId);

      if (deleteError) {
        throw deleteError;
      }
    } catch (error: any) {
      console.error('[PURCHASE SERVICE] Error in deletePaymentDirect:', error);
      throw error;
    }
  },
};
